import { waitForShoplift } from '../../../../../helpers/Shoplift';
import { revealShopifyCollection } from './skeletons';
import { loadShopifyCollectionModules } from './shopify';

export const CONSTRUCTOR_AB_COLLECTION_SELECTOR =
  '[data-constructor-ab-collection]';
const CONSTRUCTOR_AB_COLLECTION_ATTRIBUTE = 'data-constructor-ab-collection';
const CONSTRUCTOR_COLLECTION_ATTRIBUTE = 'data-constructor-widget-collection';
const CONSTRUCTOR_PRIORITY_COLLECTION_ATTRIBUTE =
  'data-constructor-priority-collection';
const CONSTRUCTOR_SHOPLIFT_ID_ATTRIBUTE =
  'data-constructor-collection-shoplift-id';
const CONSTRUCTOR_EXPERIENCE_ATTRIBUTE =
  'data-constructor-collection-experience';
const SHOPLIFT_DECISION_TIMEOUT = 3000;
const decisions = new WeakMap();

const findCollections = (root) => {
  const collections = [];

  if (!root) {
    return collections;
  }

  if (root.matches?.(CONSTRUCTOR_AB_COLLECTION_SELECTOR)) {
    collections.push(root);
  }

  root
    .querySelectorAll?.(CONSTRUCTOR_AB_COLLECTION_SELECTOR)
    .forEach((element) => collections.push(element));

  return collections;
};

const dispatchActivation = (element) => {
  const currentWindow = element?.ownerDocument?.defaultView || window;

  element.dispatchEvent(
    new currentWindow.CustomEvent('constructorio:collection:activate', {
      bubbles: true,
      detail: { root: element },
    })
  );
};

/** Switches a pending A/B collection to the Constructor treatment arm. */
export const activateConstructorCollection = (element) => {
  const collectionId = element?.getAttribute?.(
    CONSTRUCTOR_AB_COLLECTION_ATTRIBUTE
  );

  if (!collectionId) {
    return false;
  }

  element.setAttribute(CONSTRUCTOR_COLLECTION_ATTRIBUTE, collectionId);
  element.setAttribute(CONSTRUCTOR_PRIORITY_COLLECTION_ATTRIBUTE, collectionId);
  element.removeAttribute(CONSTRUCTOR_AB_COLLECTION_ATTRIBUTE);
  element.setAttribute(CONSTRUCTOR_EXPERIENCE_ATTRIBUTE, 'constructor');
  dispatchActivation(element);

  return true;
};

/** Switches a pending A/B collection to the already-rendered Shopify arm. */
export const activateShopifyCollection = (element, reason = 'ab-control') => {
  if (!element) {
    return Promise.resolve(false);
  }

  element.removeAttribute(CONSTRUCTOR_AB_COLLECTION_ATTRIBUTE);
  element.removeAttribute(CONSTRUCTOR_COLLECTION_ATTRIBUTE);
  element.removeAttribute(CONSTRUCTOR_PRIORITY_COLLECTION_ATTRIBUTE);
  element.setAttribute(CONSTRUCTOR_EXPERIENCE_ATTRIBUTE, 'shopify');
  revealShopifyCollection(element, reason);

  return loadShopifyCollectionModules(element).catch((error) => {
    console.error('Shopify collection controller error', error);
    return false;
  });
};

export const decideCollectionExperience = async (
  element,
  constructorEnabled
) => {
  if (!constructorEnabled) {
    await activateShopifyCollection(element, 'constructor-disabled');
    return 'shopify';
  }

  const currentWindow = element?.ownerDocument?.defaultView || window;

  if (
    currentWindow.constructor_collection === true ||
    currentWindow.Shopify?.previewMode === true
  ) {
    activateConstructorCollection(element);
    return 'constructor';
  }

  const shopliftId = element
    ?.getAttribute?.(CONSTRUCTOR_SHOPLIFT_ID_ATTRIBUTE)
    ?.trim();

  if (!shopliftId) {
    await activateShopifyCollection(element, 'missing-shoplift-id');
    return 'shopify';
  }

  try {
    const shoplift = await waitForShoplift({
      timeout: SHOPLIFT_DECISION_TIMEOUT,
    });
    const active =
      shoplift &&
      (await Promise.resolve(shoplift.isHypothesisActive(shopliftId)));

    if (active === true) {
      activateConstructorCollection(element);
      return 'constructor';
    }
  } catch (error) {
    console.error('Constructor collection Shoplift decision failed', error);
  }

  await activateShopifyCollection(element, 'ab-control');
  return 'shopify';
};

/** Resolves every pending collection once and observes Theme Editor inserts. */
export const initConstructorCollectionExperiences = (
  root = typeof document !== 'undefined' ? document : null,
  { constructorEnabled = true } = {}
) => {
  const decide = (element) => {
    if (!decisions.has(element)) {
      decisions.set(
        element,
        decideCollectionExperience(element, constructorEnabled)
      );
    }

    return decisions.get(element);
  };

  const initialDecisions = findCollections(root).map(decide);
  const currentDocument = typeof document !== 'undefined' ? document : null;

  if (
    !currentDocument ||
    root !== currentDocument ||
    typeof MutationObserver !== 'function'
  ) {
    return Promise.all(initialDecisions);
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        findCollections(node).forEach(decide);
      });
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  return Promise.all(initialDecisions);
};

export default initConstructorCollectionExperiences;
